import {Badge, NumberInput, TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {List} from 'src/design/atoms/List';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React, {useCallback, useEffect, useState} from 'react';
import {SectionCard} from 'src/design/atoms/SectionCard';
import type {IFormDataV2} from 'src/types/FormV2';
import {z} from 'zod';

const RiskValueMatrixSchema = z
  .object({
    lowName: z
      .string()
      .min(1, 'Low level name is required')
      .max(25, 'Low level name must be less than 25 characters'),
    lowMaxRange: z
      .number({invalid_type_error: 'Must be a number'})
      .min(2, 'Low/Mid boundary must be at least 2')
      .max(23, 'Low/Mid boundary must be less than 24'),
    midName: z
      .string()
      .min(1, 'Mid level name is required')
      .max(25, 'Mid level name must be less than 25 characters'),
    midMaxRange: z
      .number({invalid_type_error: 'Must be a number'})
      .min(3, 'Mid/High boundary must be at least 3')
      .max(24, 'Mid/High boundary must be less than 25'),
    highName: z
      .string()
      .min(1, 'High level name is required')
      .max(25, 'High level name must be less than 25 characters'),
  })
  .refine(data => data.lowMaxRange < data.midMaxRange, {
    message: 'Low/Mid boundary must be less than Mid/High boundary',
    path: ['lowMaxRange'],
  });

export interface RiskMatrixSectionProps {
  formData: IFormDataV2;
  onChange: (name: string, value: any) => void;
  mode: 'create' | 'edit' | 'view';
  validationFnRef?: React.MutableRefObject<(() => boolean) | undefined>;
}

export const RiskMatrixSection: React.FC<RiskMatrixSectionProps> = ({
  formData,
  onChange,
  mode,
  validationFnRef,
}) => {
  const isViewMode = mode === 'view';
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasInteracted, setHasInteracted] = useState(false);

  const validateAll = useCallback(() => {
    const result = RiskValueMatrixSchema.safeParse(formData);

    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.issues.forEach(issue => {
        const path = issue.path[0];
        if (path) {
          newErrors[path.toString()] = issue.message;
        }
      });
      setErrors(newErrors);
      return false;
    } else {
      setErrors({});
    }
    return true;
  }, [formData]);

  useEffect(() => {
    if (hasInteracted) {
      validateAll();
    }
  }, [formData, hasInteracted, validateAll]);

  useEffect(() => {
    if (validationFnRef) {
      validationFnRef.current = () => {
        setHasInteracted(true);
        return validateAll();
      };
    }
  }, [validationFnRef, validateAll]);

  const handleTextChange = useCallback(
    (name: string, value: string) => {
      setHasInteracted(true);
      onChange(name, value);
    },
    [onChange]
  );

  const handleBoundaryChange = useCallback(
    (boundary: 'lowMid' | 'midHigh', value: string | number) => {
      setHasInteracted(true);
      const val = Number(value);
      if (isNaN(val)) return;

      if (boundary === 'lowMid') {
        // User sets where Low ends; Mid starts at next value
        onChange('lowMaxRange', val);
        onChange('midMinRange', val + 1);
      } else {
        // User sets where Mid ends; High starts at next value
        onChange('midMaxRange', val);
        onChange('highMinRange', val + 1);
      }
    },
    [onChange]
  );

  const getError = (field: string): string | undefined =>
    hasInteracted ? errors[field] : undefined;

  // Derived display values
  const lowMin = 1;
  const lowMax = formData.lowMaxRange ?? '';
  const midMin = typeof lowMax === 'number' ? lowMax + 1 : '';
  const midMax = formData.midMaxRange ?? '';
  const highMin = typeof midMax === 'number' ? midMax + 1 : '';
  const highMax = 25;

  const LEVELS = [
    {
      key: 'low',
      label: 'Low',
      color: 'green',
      nameField: 'lowName',
      range: `${lowMin} — ${lowMax || '?'}`,
    },
    {
      key: 'mid',
      label: 'Mid',
      color: 'yellow',
      nameField: 'midName',
      range: `${midMin || '?'} — ${midMax || '?'}`,
    },
    {
      key: 'high',
      label: 'High',
      color: 'red',
      nameField: 'highName',
      range: `${highMin || '?'} — ${highMax}`,
    },
  ];

  if (isViewMode) {
    return (
      <SectionCard
        title="Risk Matrix"
        data-testid="section-risk-matrix">
        <Box p="md">
          <Stack gap="xs">
            {LEVELS.map(level => (
              <Group
                key={level.key}
                gap="sm"
                data-testid={`risk-level-${level.key}`}>
                <Badge
                  size="lg"
                  color={level.color}
                  variant="light"
                  w={100}
                  aria-label={`${level.label} risk level`}>
                  {formData[level.nameField] || level.label}
                </Badge>
                <Text size="sm" fw={500}>
                  Score {level.range}
                </Text>
              </Group>
            ))}
          </Stack>
        </Box>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Risk Matrix"
      data-testid="section-risk-matrix">
      <Box p="md">
        <Text size="sm" c="dimmed" mb="md">
          Define the score ranges and labels for Low, Mid, and High risk
          levels. Scores are calculated as Probability x Severity (range
          1-25). Set the two boundary points and the ranges will be calculated
          automatically.
        </Text>

        <Stack gap="md">
          {/* Level names */}
          <Grid gutter="sm">
            {LEVELS.map(level => (
              <Grid.Col key={level.key} span={{base: 12, md: 4}}>
                <TextInput
                  data-testid={`form-field-${level.nameField}`}
                  label={
                    <Group gap="xs">
                      <Badge
                        size="xs"
                        color={level.color}
                        variant="filled"
                        style={{verticalAlign: 'middle'}}>
                        {level.label}
                      </Badge>
                      <span>Level Name</span>
                    </Group>
                  }
                  value={formData[level.nameField] || ''}
                  onChange={e =>
                    handleTextChange(
                      level.nameField,
                      e.currentTarget.value
                    )
                  }
                  placeholder={level.label}
                  withAsterisk
                  error={getError(level.nameField)}
                />
              </Grid.Col>
            ))}
          </Grid>

          {/* Boundary inputs */}
          <Grid gutter="sm">
            <Grid.Col span={{base: 12, md: 6}}>
              <NumberInput
                data-testid="form-field-lowMaxRange"
                label="Low / Mid Boundary"
                description={`Low: 1 — ${lowMax || '?'}, Mid starts at: ${midMin || '?'}`}
                value={formData.lowMaxRange ?? ''}
                onChange={value =>
                  handleBoundaryChange('lowMid', value)
                }
                min={2}
                max={23}
                withAsterisk
                error={getError('lowMaxRange')}
              />
            </Grid.Col>
            <Grid.Col span={{base: 12, md: 6}}>
              <NumberInput
                data-testid="form-field-midMaxRange"
                label="Mid / High Boundary"
                description={`Mid ends at: ${midMax || '?'}, High: ${highMin || '?'} — 25`}
                value={formData.midMaxRange ?? ''}
                onChange={value =>
                  handleBoundaryChange('midHigh', value)
                }
                min={3}
                max={24}
                withAsterisk
                error={getError('midMaxRange')}
              />
            </Grid.Col>
          </Grid>

          {/* Visual range summary */}
          <Box>
            <Text size="sm" fw={500} mb="xs">
              Score Ranges
            </Text>
            <Group gap="sm">
              {LEVELS.map(level => (
                <Badge
                  key={level.key}
                  size="lg"
                  color={level.color}
                  variant="light"
                  data-testid={`risk-badge-${level.key}`}
                  aria-label={`${level.label} risk level indicator`}>
                  {formData[level.nameField] || level.label}: {level.range}
                </Badge>
              ))}
            </Group>
          </Box>
        </Stack>

        {hasInteracted && Object.keys(errors).length > 0 && (
          <Alert color="red" mt="md" data-testid="matrix-validation-errors">
            <Text size="sm" fw={500} mb="xs">
              Please fix the following:
            </Text>
            <List size="sm">
              {Object.values(errors).map((error, index) => (
                <List.Item key={index}>{error}</List.Item>
              ))}
            </List>
          </Alert>
        )}
      </Box>
    </SectionCard>
  );
};
